import { Flex, Icon, Text } from "@repo/ui";
import { useEffect, useState } from "react";

type ImageData = {
	key: string;
	signedUrl: string;
	expiresAt: Date;
};

type InsuranceCardImageModalProps = {
	images: ImageData[];
	isOpen: boolean;
	onClose: () => void;
	insuranceName: string;
};

export function InsuranceCardImageModal({
	images,
	isOpen,
	onClose,
	insuranceName,
}: InsuranceCardImageModalProps) {
	const [currentImageIndex, setCurrentImageIndex] = useState(0);
	const [imageLoadError, setImageLoadError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [preloadedImageSrc, setPreloadedImageSrc] = useState<string | null>(
		null,
	);

	// Preload image when modal opens or image changes
	useEffect(() => {
		if (isOpen && images.length > 0) {
			setIsLoading(true);
			setImageLoadError(null);
			setPreloadedImageSrc(null);

			const currentImage = images[currentImageIndex];

			const img = new Image();
			img.onload = () => {
				setPreloadedImageSrc(img.src);
				setIsLoading(false);
				setImageLoadError(null);
			};
			img.onerror = () => {
				setImageLoadError(`Failed to load image: ${currentImage?.key}`);
				setIsLoading(false);
			};
			img.src = currentImage?.signedUrl || "";
		}
	}, [currentImageIndex, images, isOpen]);

	if (!isOpen || images.length === 0) return null;

	const currentImage = images[currentImageIndex];

	if (!currentImage) return null;

	return (
		<div
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				backgroundColor: "rgba(0, 0, 0, 0.8)",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				zIndex: 1000,
			}}
			onClick={onClose}
		>
			<div
				style={{
					backgroundColor: "white",
					borderRadius: "12px",
					maxWidth: "80vw",
					maxHeight: "80vh",
					padding: "24px",
					position: "relative",
					boxShadow:
						"0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
				}}
				onClick={(e) => e.stopPropagation()}
			>
				<Flex column gap="lg">
					<div
						style={{
							position: "absolute",
							top: "16px",
							right: "16px",
							cursor: "pointer",
							padding: "8px",
							borderRadius: "6px",
							backgroundColor: "rgba(0, 0, 0, 0.05)",
						}}
						onClick={onClose}
					>
						<Icon name="X" size="sm" />
					</div>

					<Text size="lg" weight="medium" style={{ marginRight: "40px" }}>
						{insuranceName} Insurance Card
					</Text>

					{imageLoadError ? (
						<Flex column align="center" gap="md" padding="xl">
							<Icon name="AlertCircle" size="sm" color="red11" />
							<Text color="red11">{imageLoadError}</Text>
						</Flex>
					) : isLoading ? (
						<Flex align="center" justify="center" padding="xl">
							<Text size="md" color="sand11">
								Loading image...
							</Text>
						</Flex>
					) : preloadedImageSrc ? (
						<img
							src={preloadedImageSrc}
							alt={`${insuranceName} insurance card`}
							style={{
								maxWidth: "100%",
								maxHeight: "60vh",
								objectFit: "contain",
								borderRadius: "8px",
							}}
						/>
					) : null}

					{images.length > 1 && (
						<Flex align="center" justify="center" gap="md">
							<div
								style={{ cursor: "pointer", padding: "4px" }}
								onClick={() =>
									setCurrentImageIndex(
										currentImageIndex > 0
											? currentImageIndex - 1
											: images.length - 1,
									)
								}
							>
								<Icon
									name="ChevronDown"
									size="sm"
									style={{ transform: "rotate(90deg)" }}
								/>
							</div>
							<Text size="md" color="sand11">
								{currentImageIndex + 1} of {images.length}
							</Text>
							<div
								style={{ cursor: "pointer", padding: "4px" }}
								onClick={() =>
									setCurrentImageIndex(
										currentImageIndex < images.length - 1
											? currentImageIndex + 1
											: 0,
									)
								}
							>
								<Icon
									name="ChevronDown"
									size="sm"
									style={{ transform: "rotate(-90deg)" }}
								/>
							</div>
						</Flex>
					)}
				</Flex>
			</div>
		</div>
	);
}
